/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { isArray } from '@ember/array';
import DS from 'ember-data';
import { isEmpty } from '@ember/utils';
import config from 'cio/config/environment';
import AnalyticsService from 'cio/services/analytics';
import ApiService, { ApiError } from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import AssetModel, { AssetsQueryParams, ASSETS_DEFAULT_SORT_BY } from 'cio/models/asset';
import { getFileExtension } from 'cio/utils/uploads';
import { EventsType } from 'cio/utils/analytics/events';
import pluralize from 'cio/utils/pluralize';
import joinWords from 'cio/utils/join-words';
import b64EncodeUnicode from 'cio/utils/base64-unicode';

const PDF_UNSELECTABLE_REASON = 'PDFs cannot be selected as images.';
const IMAGE_SIZE_UNSELECTABLE_REASON = 'Image is larger than acceptable size limit.';

export type FolderPath = {
  name: string;
  id: string | null;
};

type ErrorMessage = {
  message: string;
};

interface AssetLibraryArgs {
  folderId?: string | null;
  page?: number;
  search?: string;
  sortBy?: string;
  sortAsc?: boolean;
  onParamsChange: (params: AssetsQueryParams) => unknown;
  onSelectAsset?: (asset: AssetModel) => unknown;
  onFileUploadStarted?: () => unknown;
  onAllFilesUploadFinished?: () => unknown;
  builderType?: string | null;
  hideSubheading?: boolean;

  filterFileSizeInBytes?: number;
  filterPdfs?: boolean;
}

const INVALID_SEARCH_TERM_LENGTH_ERROR = 'invalid search term length';
const DEBOUNCE_DURATION = config.APP.debounceDuration;
const UPLOAD_DROP_DRAG_DELAY = config.APP.shortDebounceDuration;
const TOP_LEVEL_FOLDER_PATH: FolderPath = { name: 'All files', id: null };

export default class AssetLibrary extends Component<AssetLibraryArgs> {
  @service store!: DS.Store;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;

  @tracked assets: AssetModel[] = [];
  @tracked checkedAssetIds: string[] = [];
  @tracked itemsPerPage = 0;
  @tracked totalRecords = 0;
  @tracked createFolderErrors: ErrorMessage[] = [];
  @tracked showCreateFolderModal = false;
  @tracked showDeleteAssetModal = false;
  @tracked newFolderName = '';
  @tracked assetsToDelete: AssetModel[] = [];
  @tracked isFilteringByBytesEnabled = true;
  @tracked viewToggleValue: 'grid' | 'list' = 'grid';
  @tracked hasSearchLengthError = false;
  @tracked folderPaths: FolderPath[] = [TOP_LEVEL_FOLDER_PATH];
  @tracked isDroppingUpload = false;
  @tracked dropFilesToUpload: File[] = [];
  @tracked unselectableAssetReasons: Record<string, string> = {};

  dropElement: HTMLDivElement | null = null;
  emptySearchImage = new URL('cio/assets/images/illustrations/empty-search.png', import.meta.url).toString();

  constructor(owner: unknown, args: AssetLibraryArgs) {
    super(owner, args);

    this.fetchAssetsForInitialization();
  }

  willDestroy() {
    super.willDestroy();

    this.dropElement?.removeEventListener('dragover', this.handleUploadDragOver);
    this.dropElement?.removeEventListener('dragleave', this.handleUploadDragLeave);
    this.dropElement?.removeEventListener('drop', this.handleUploadDrop);
  }

  @action
  setUploadDropEventListeners(el: HTMLDivElement) {
    this.dropElement = el;

    this.dropElement?.addEventListener('dragover', this.handleUploadDragOver);
    this.dropElement?.addEventListener('dragleave', this.handleUploadDragLeave);
    this.dropElement?.addEventListener('drop', this.handleUploadDrop);
  }

  setUploadDropState = task({ restartable: true }, async (isDropping: boolean, isImmediate?: boolean) => {
    if (isImmediate === true) {
      this.isDroppingUpload = isDropping;
      return;
    }

    await timeout(UPLOAD_DROP_DRAG_DELAY);
    this.isDroppingUpload = isDropping;
  });

  private isDragEventForUpload(event: DragEvent) {
    return event.dataTransfer?.types.includes('Files') === true;
  }

  @action
  handleUploadDragOver(event: DragEvent) {
    if (!this.isDragEventForUpload(event)) {
      return;
    }

    event.preventDefault();

    // display "drop here" content mask immediately, but delay hiding it
    let isImmediate = !this.isDroppingUpload;

    this.setUploadDropState.perform(true, isImmediate);
  }

  @action
  handleUploadDragLeave(event: DragEvent) {
    if (!this.isDragEventForUpload(event)) {
      return;
    }

    event.preventDefault();
    this.setUploadDropState.perform(false);
  }

  @action
  handleUploadDrop(event: DragEvent) {
    if (!this.isDragEventForUpload(event)) {
      return;
    }

    event.preventDefault();
    this.setUploadDropState.perform(false, true);

    if (event.dataTransfer == null) {
      return;
    }

    let files: File[] = [];
    if (event.dataTransfer.items != null) {
      files = [...event.dataTransfer.items].reduce((items: File[], item: DataTransferItem) => {
        if (item?.kind === 'file') {
          let file = item.getAsFile();
          if (file != null) {
            items.push(file);
          }
        }

        return items;
      }, []);
    } else if (event.dataTransfer.files != null) {
      files = [...event.dataTransfer.files];
    }

    this.dropFilesToUpload = files;
  }

  get builderType() {
    return this.args.builderType ?? '';
  }

  @computed('assets', 'isDisplayingSearchResults', 'isFilteringByBytesEnabled')
  get filteredAssets() {
    this.unselectableAssetReasons = {};
    return this.assets.filter((asset: AssetModel) => {
      if (asset.id === this.args.folderId) {
        return false;
      }

      if (!this.isAssetWithinByteLimit(asset)) {
        this.unselectableAssetReasons[asset.id] = IMAGE_SIZE_UNSELECTABLE_REASON;
      }

      if (this.args.filterPdfs === true && asset.isPdf) {
        this.unselectableAssetReasons[asset.id] = PDF_UNSELECTABLE_REASON;
      }

      if (this.isDisplayingSearchResults) {
        return true;
      }

      return asset.parentId === this.args.folderId || (asset.parentId == null && this.args.folderId == null);
    });
  }

  isAssetWithinByteLimit(asset: AssetModel) {
    if (asset.isDir || this.args.filterFileSizeInBytes == null || !this.isFilteringByBytesEnabled) {
      return true;
    }

    return asset.size <= this.args.filterFileSizeInBytes;
  }

  get currentFolder() {
    return this.folderPaths[this.folderPaths.length - 1];
  }

  @computed('args.search')
  get isDisplayingSearchResults() {
    return !isEmpty(this.args.search);
  }

  @computed('args.search', 'filteredAssets')
  get hasEmptySearchResult() {
    return this.isDisplayingSearchResults && isEmpty(this.filteredAssets);
  }

  @computed('args.{folderId,page,sortBy,sortAsc,search}')
  get currentParams(): AssetsQueryParams {
    return {
      folderId: this.args.folderId ?? null,
      page: this.args.page ?? 1,
      sortBy: this.args.sortBy ?? ASSETS_DEFAULT_SORT_BY,
      sortAsc: this.args.sortAsc === true,
      search: this.args.search ?? '',
    };
  }

  @action
  onPageChange(value: number) {
    this.args.onParamsChange({
      ...this.currentParams,
      page: value,
    });
  }

  @action
  onSortByChange(value: string) {
    this.args.onParamsChange({
      ...this.currentParams,
      sortBy: value,
      page: 1,
    });
  }

  @action
  onSortDirectionChange() {
    this.args.onParamsChange({
      ...this.currentParams,
      sortAsc: !this.currentParams.sortAsc,
      page: 1,
    });
  }

  @action
  onFolderPathNavigation(assetId: string) {
    this.hasSearchLengthError = false;
    this.args.onParamsChange({
      ...this.currentParams,
      folderId: assetId,
      page: 1,
      search: '',
    });
  }

  @action
  onSearchChange(search: string) {
    this.startSearchTask.perform(search);
  }

  @action
  onFileUploadStarted(folderId: string | null) {
    this.trackUploadStarted(folderId);
    this.args.onFileUploadStarted?.();
  }

  @action
  onAllFilesUploadFinished() {
    this.args.onParamsChange({
      ...this.currentParams,
      search: '',
      page: 1,
    });

    this.args.onAllFilesUploadFinished?.();
  }

  startSearchTask = task({ restartable: true }, async (search: string) => {
    await timeout(DEBOUNCE_DURATION);

    if (search.length > 0 && search.length < 3) {
      this.hasSearchLengthError = true;
      return;
    }

    this.hasSearchLengthError = false;
    this.args.onParamsChange({
      ...this.currentParams,
      page: 1,
      search,
    });
  });

  fetchAssetsTask = task({ keepLatest: true }, async () => {
    this.clearCheckedAssets();

    try {
      let assets = await this.store.query('asset', {
        folder_id: this.currentParams.folderId,
        page: this.currentParams.page,
        sort: this.currentParams.sortBy,
        order: this.currentParams.sortAsc ? 'asc' : 'desc',
        search: b64EncodeUnicode(this.currentParams.search),
      });
      this.assets = assets.toArray();
      this.folderPaths = this.getCurrentFolderPaths();

      let { size, total } = (assets as any).meta.pagination;
      this.itemsPerPage = size;
      this.totalRecords = total;

      let lastPage = Math.ceil(total / size);
      if (lastPage > 0 && this.currentParams.page > lastPage) {
        this.args.onParamsChange({
          ...this.currentParams,
          page: lastPage,
        });
      }
    } catch (error) {
      this.assets = [];

      let errors = (error.errors as ApiError[] | null) ?? [];
      let [firstError] = errors;
      if (firstError?.detail === INVALID_SEARCH_TERM_LENGTH_ERROR) {
        this.hasSearchLengthError = true;
      } else if (firstError?.status === '404' && this.currentParams.folderId != null) {
        // Requested folder might have been deleted.
        this.args.onParamsChange({
          ...this.currentParams,
          folderId: null,
        });
      } else {
        this.apiErrors.report(error);
        this.alerts.error(
          'assetLibrary.fetch.failure',
          'Something went wrong when retrieving assets. Please try again or contact support for help.',
        );
      }
    }
  });

  getCurrentFolderPaths() {
    let paths: FolderPath[] = [];

    let parentFolderId: string | null = this.args.folderId ?? null;
    while (parentFolderId != null) {
      let parentFolder: AssetModel | null = this.store.peekRecord('asset', parentFolderId);
      parentFolderId = parentFolder?.parentId ?? null;
      if (parentFolder != null) {
        paths.unshift({ name: parentFolder.name, id: parentFolder.id });
      }
    }

    paths.unshift(TOP_LEVEL_FOLDER_PATH);
    return paths;
  }

  @action
  async fetchAssetsForInitialization() {
    await this.fetchAssetsTask.perform();

    if (this.args.folderId != null) {
      let currentFolder = this.assets.find((a) => a.id === this.args.folderId);
      if (currentFolder == null) {
        this.args.onParamsChange({
          ...this.currentParams,
          folderId: null,
          page: 1,
        });
      }
    }

    this.trackView(false);
  }

  @action
  resetErrors() {
    this.createFolderErrors = [];
    this.alerts.clearAlerts();
  }

  @action
  onOpenCreateFolderModal() {
    this.showCreateFolderModal = true;
    this.trackFolderAddStarted();
  }

  @action
  onCloseCreateFolderModal(track = false) {
    this.newFolderName = '';
    this.createFolderErrors = [];
    this.showCreateFolderModal = false;

    if (track) {
      this.trackFolderAddCanceled();
    }
  }

  @action
  onSubmitCreateFolder(event: Event) {
    event.preventDefault();
    this.createFolderErrors = [];
    this.createFolderTask.perform();
  }

  createFolderTask = task(async () => {
    let newAsset = this.store.createRecord('asset', {
      name: this.newFolderName,
      isDir: true,
      parentId: this.args.folderId,
    });

    try {
      await newAsset.save();

      this.args.onParamsChange({
        ...this.currentParams,
        search: '',
        page: 1,
      });

      this.onCloseCreateFolderModal();
    } catch (err) {
      newAsset.unloadRecord();

      if (isArray(err?.errors) && err.errors[0]?.status === '400') {
        this.createFolderErrors = [{ message: err.errors[0].detail }];

        return;
      }

      this.apiErrors.report(err);
    }
  });

  @action
  onSelectAsset(asset: AssetModel) {
    if (this.checkedAssetIds.length > 0) {
      // don't allow any other actions if there are checked assets
      return;
    }

    if (this.unselectableAssetReasons[asset.id] != null) {
      return;
    }

    if (asset.isDir) {
      this.args.onParamsChange({
        ...this.currentParams,
        folderId: asset.id,
        page: 1,
        search: '',
      });
    } else if (this.args.onSelectAsset != null) {
      this.args.onSelectAsset(asset);
    }
  }

  @action
  onCheckAsset(checked: boolean, asset: AssetModel) {
    if (checked) {
      if (this.checkedAssetIds.indexOf(asset.id) === -1) {
        this.checkedAssetIds = [...this.checkedAssetIds, asset.id];
      }
    } else {
      this.checkedAssetIds = this.checkedAssetIds.filter((a) => a !== asset.id);
    }
  }

  @action
  clearCheckedAssets() {
    if (this.checkedAssetIds.length > 0) {
      this.checkedAssetIds = [];
    }
  }

  @action
  checkAllAssets() {
    this.checkedAssetIds = this.filteredAssets
      .filter((a) => a.isDeletable && (this.args.filterPdfs !== true || !a.isPdf))
      .map((a) => a.id);
  }

  get deleteAssetDetails() {
    let details = { imageCount: 0, folderCount: 0, title: '', text: '' };

    if (this.assetsToDelete.length === 0) {
      return details;
    }

    if (this.assetsToDelete.length === 1) {
      let [asset] = this.assetsToDelete;
      details.text = asset.name;

      if (asset.isDir) {
        details.title = 'folder';
        ++details.folderCount;
      } else {
        details.title = 'image';
        ++details.imageCount;
      }
    } else {
      details.title = 'multiple assets';
      let countTexts = [];

      this.assetsToDelete.forEach((a) => (a.isDir ? ++details.folderCount : ++details.imageCount));

      if (details.imageCount) {
        countTexts.push(`${details.imageCount} ${pluralize(details.imageCount, 'image')}`);
      }

      if (details.folderCount) {
        countTexts.push(`${details.folderCount} ${pluralize(details.folderCount, 'folder')}`);
      }

      details.text = joinWords(countTexts);
    }

    return details;
  }

  @action
  onOpenDeleteAssetModal(asset: AssetModel) {
    this.assetsToDelete = [asset];
    this.showDeleteAssetModal = true;

    this.trackAssetDeleteStarted(this.assetsToDelete);
  }

  @action
  onOpenDeleteCheckedAssetsModal() {
    if (this.checkedAssetIds.length < 1) {
      return;
    }

    this.assetsToDelete = this.assets.filter((a) => this.checkedAssetIds.includes(a.id));
    this.showDeleteAssetModal = true;

    this.trackAssetDeleteStarted(this.assetsToDelete);
  }

  @action
  onCloseDeleteAssetModal() {
    this.assetsToDelete = [];
    this.showDeleteAssetModal = false;
  }

  @action
  onDeleteAssetModalDeleteButtonClick() {
    if (this.assetsToDelete.length > 0) {
      this.deleteAssetsTask.perform(this.assetsToDelete);
    }
  }

  deleteAssetsTask = task(async (assets: AssetModel[]) => {
    try {
      await this.api.delete('assets', { data: { asset_ids: assets.map((a) => parseInt(a.id)) } });
      this.fetchAssetsTask.perform();
    } catch (err) {
      this.apiErrors.report(err);
    }

    this.onCloseDeleteAssetModal();
  });

  get draggableOptions() {
    return {
      clone: {
        cloneElement: '.js-move-checked-assets-mirror',
      },
    };
  }

  @action
  onBeforeDragStart(asset: AssetModel) {
    this.onCheckAsset(true, asset);
  }

  @action
  onDrop(targetParent: AssetModel | null) {
    if (this.checkedAssetIds.length < 1) {
      // need something checked to drop
      return;
    }

    if (targetParent != null && targetParent.isDir !== true) {
      // has to be root (null) or a directory (isDir)
      return;
    }

    this.moveAssetsTasks.perform(targetParent?.id ?? null, this.checkedAssetIds);
  }

  moveAssetsTasks = task(async (parentId: string | null, assetIds: string[]) => {
    try {
      let assetsToMove = this.assets.filter((a) => assetIds.includes(a.id));
      await this.api.put('assets', {
        data: {
          assets: assetsToMove.map((a) => ({
            asset: {
              ...a.serialize({ includeId: true }),
              parent_id: parentId,
            },
          })),
        },
      });

      this.checkedAssetIds = [];

      this.fetchAssetsTask.perform();
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  //#region tracking
  getAssetDirectoryAnalyticsData(isParent: boolean, folderId?: string | null) {
    let nameKey = 'folderName';
    let idKey = 'folderId';

    if (isParent) {
      nameKey = 'parentFolderName';
      idKey = 'parentFolderId';
    }

    let name = '';
    let id: string | null = null;

    if (folderId === undefined) {
      name = this.currentFolder.name;
      id = this.currentFolder.id;
    } else if (folderId === null) {
      name = TOP_LEVEL_FOLDER_PATH.name;
      id = TOP_LEVEL_FOLDER_PATH.id;
    } else {
      let folderAsset = this.store.peekRecord('asset', folderId);
      if (folderAsset) {
        name = folderAsset.name;
        id = folderAsset.id;
      }
    }

    return {
      builderType: this.builderType,
      [nameKey]: name,
      [idKey]: id ?? '',
    };
  }

  getAssetAnalyticsData(asset: AssetModel) {
    return {
      builderType: this.builderType,
      assetId: asset.id,
      fileType: getFileExtension(asset.name),
      folderName: this.currentFolder.name,
      folderId: this.currentFolder.id ?? '',
    };
  }

  @action
  trackUploadStarted(folderId: string | null) {
    this.analytics.track('asset_library', 'upload_image_started', this.getAssetDirectoryAnalyticsData(false, folderId));
  }

  @action
  trackFolderAddStarted() {
    this.analytics.track('asset_library', 'folder_add_started', this.getAssetDirectoryAnalyticsData(true));
  }

  @action
  trackFolderAddCanceled() {
    this.analytics.track('asset_library', 'folder_add_canceled', this.getAssetDirectoryAnalyticsData(true));
  }

  @action
  trackView(isFolderView: boolean) {
    let eventName: keyof EventsType['asset_library'] = 'viewed';

    let folderCount = 0;
    let assetCount = 0;
    this.filteredAssets.forEach((a) => {
      if (a.isDir) {
        ++folderCount;
      } else {
        ++assetCount;
      }
    });

    let payload: Record<string, string | number> = {
      builderType: this.builderType,
      folderCount,
      assetCount,
    };

    if (isFolderView) {
      eventName = 'folder_viewed';
      payload.folderName = this.currentFolder.name;
      payload.folderId = this.currentFolder.id ?? '';
    }

    this.analytics.track('asset_library', eventName, payload);
  }

  @action
  trackAssetCopy(asset: AssetModel) {
    this.analytics.track('asset_library', 'file_url_copied', this.getAssetAnalyticsData(asset));
  }

  @action
  trackAssetPreview(asset: AssetModel) {
    this.analytics.track('asset_library', 'file_previewed', this.getAssetAnalyticsData(asset));
  }

  @action
  trackAssetDeleteStarted(assets: AssetModel[]) {
    let [asset] = assets;

    if (assets.length > 1) {
      this.analytics.track('asset_library', 'bulk_delete_started', {
        count: assets.length,
        ...this.getAssetDirectoryAnalyticsData(true),
      });
      return;
    }

    if (asset.isDir) {
      this.analytics.track('asset_library', 'folder_delete_started', {
        folderName: asset.name,
        folderId: asset.id,
        ...this.getAssetDirectoryAnalyticsData(true),
      });
    } else {
      this.analytics.track('asset_library', 'file_delete_started', this.getAssetAnalyticsData(asset));
    }
  }
  //#endregion
}
